import React, { useEffect, useRef, useState } from "react";
import { Button } from "@material-ui/core";
import DataTable from "@/components/DataTable";
import { useTranslation } from "@/langs/useTranslation";
import { formatDate } from "./datetimeHandlers";
import { ReactRender } from "@/components/ReactRender";
import { useRequest } from "ahooks";
import { useFormik } from "formik";
import * as dayjs from "dayjs";
import useStyles from "@/styles/alarmStyle";
import { FormItem } from "@/components/Form";
import DeviceLogStatus from "@/components/FormFields/DeviceLogStatus";
import DateRangePicker from "@/components/DateRangePicker";
import TemplateList from "@/components/FormFields/TemplateList";
import TemplateTypeList from "@/components/FormFields/TemplateTypeList";
import CollapsePanel, {
  CollapseItem,
  CollapseTrigger
} from "@/components/CollapsePanel";
import { getDeviceLogs, getTemplateEventList } from "@/apis/templateApi";

const TaskType = ({ data, rowData }) => {
  const { t } = useTranslation("device");

  return (
    <div className="task_name">
      {data === "device_power" &&
      rowData.json &&
      rowData.json.system_booting_type ? (
        <>
          {t(`tasks.${data}`)} - [{" "}
          {t(`tasks.${rowData.json.system_booting_type}`)} ]
        </>
      ) : (data === "lock_usb" ||
          data === "disable_combination_key" ||
          data === "disable_windows_notify") &&
        rowData.json ? (
        t(`tasks.${data}_${rowData.json.status}`)
      ) : (
        t(`tasks.${data}`)
      )}
    </div>
  );
};

export const DeviceLogTable = ({ className, style, footer, children }) => {
  const { t } = useTranslation("deviceControl");
  const tableRef = useRef(null);
  const isReset = useRef(true);
  const classes = useStyles();

  const initialValues = {
    template_id: undefined,
    event_id: undefined,
    result: undefined,
    date: [null, null],
    start_time: undefined,
    end_time: undefined
  };

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });
  const { data: templateData } = useRequest(() => getTemplateEventList());
  const templateList = templateData?.list || [];

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const date = formValues.date;
    return {
      template_id: formValues.template_id,
      event_id: formValues.event_id,
      result: formValues.result,
      start_time: date[0] ? dayjs.utc(date[0]).toISOString() : undefined,
      end_time: date[1] ? dayjs.utc(date[1]).toISOString() : undefined
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    handleReset();
    handleReloadDataTable(true);
  };

  useEffect(() => {
    resetQuery();
  }, []);

  const columns = [
    {
      data: "templateName",
      orderable: false,
      title: t({ defaultMessage: "模板名称" }),
      createdCell: (td, data, rowData) => {
        ReactRender(<span>{rowData.templateName}</span>, td);
      }
    },
    {
      data: "eventName",
      orderable: false,
      title: t({ defaultMessage: "操作类型" }),
      createdCell: (td, data, rowData) => {
        ReactRender(<span>{rowData.eventName}</span>, td);
      }
    },
    {
      data: "operatorName",
      title: t({ defaultMessage: "操作者" }),
      orderable: false,
      createdCell: (td, data, rowData) => {
        ReactRender(<span>{rowData.operatorName}</span>, td);
      }
    },
    {
      data: "deviceSn",
      title: t({ defaultMessage: "设备SN" }),
      orderable: false,
      createdCell: (td, data, rowData) => {
        ReactRender(<span>{rowData.deviceSn}</span>, td);
      }
    },
    {
      data: "logDetail",
      title: t({ defaultMessage: "操作详情" }),
      orderable: false,
      createdCell: (td, data, rowData) => {
        ReactRender(<span>{rowData.logDetail}</span>, td);
      }
    },
    {
      data: "result",
      title: t({ defaultMessage: "操作结果" }),
      orderable: false,
      createdCell: (td, data, rowData) => {
        const text =
          rowData.result === "Completed"
            ? t({ defaultMessage: "成功" })
            : t({ defaultMessage: "失败" });
        ReactRender(<span>{text}</span>, td);
      }
    },
    {
      data: "createdAt",
      orderable: false,
      title: t({ defaultMessage: "发送时间" }),
      createdCell: (td, data, rowData) => {
        ReactRender(<span>{formatDate(Number(rowData.createdAt))}</span>, td);
      }
    },
    {
      data: "ip",
      title: t({ defaultMessage: "IP" }),
      orderable: false,
      createdCell: (td, data, rowData) => {
        ReactRender(<span>{rowData.ip}</span>, td);
      }
    }
  ];

  const ajax = (data, callback) => {
    return getDeviceLogs({
      ...data,
      ...getSearchParams()
    }).then(res => {
      callback({
        data: res.list,
        draw: data.draw,
        recordsFiltered: res.recordsTotal || 0,
        recordsTotal: res.recordsTotal || 0
      });
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className={classes.tableHeader}>
          <CollapsePanel>
            <div className={classes.tableQueryForm}>
              <FormItem
                label={t({ defaultMessage: "选择模板" })}
                className={classes.formItem}
              >
                <TemplateList
                  data={templateList}
                  name="template_id"
                  value={values.template_id}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "操作类型" })}
                className={classes.formItem}
              >
                <TemplateTypeList
                  data={
                    templateList.find(
                      item => item.templateId === values.template_id
                    )?.events || []
                  }
                  name="event_id"
                  value={values.event_id}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "选择结果" })}
                className={classes.formItem}
              >
                <DeviceLogStatus
                  name="result"
                  value={values.result}
                  onChange={handleChange}
                />
              </FormItem>

              <CollapseItem>
                <FormItem
                  label={t({ defaultMessage: "发送时间" })}
                  className={classes.formItem}
                >
                  <DateRangePicker
                    name="date"
                    value={values.date}
                    onChange={handleChange}
                  />
                </FormItem>
              </CollapseItem>
              <FormItem className={classes.formItem}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{ marginRight: 8 }}
                >
                  {t({ defaultMessage: "查询" })}
                </Button>
                <Button variant="outlined" onClick={resetQuery}>
                  {t({ defaultMessage: "重置" })}
                </Button>
                <CollapseTrigger />
              </FormItem>
            </div>
          </CollapsePanel>
        </div>
      </form>
      <DataTable
        ref={tableRef}
        ordering
        columns={columns}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        showCheckAll={false}
      >
        {children}
      </DataTable>
    </>
  );
};
